import {Delete, DeleteOutlineOutlined} from '@mui/icons-material';
import {Button, IconButton} from '@mui/material';
import {FC} from 'react';
import {NavigateFunction, useNavigate} from 'react-router-dom';
import {tariffsApi} from 'src/api';
import {useAppDispatch} from 'src/app/hooks';
import {AppDispatch, store} from 'src/app/store';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {TariffDTO} from 'src/types';
import {AppRouteEnum, AppRouteSpace, isErrorObject} from 'src/utils/helper';
import {FormattedMessage} from 'react-intl';
import {actionButtonSx} from 'src/components/common/actionButton.sx';
import {NotifyError} from 'src/utils/notification';

type DeleteTariffMutationTrigger = ReturnType<typeof tariffsApi.useDeleteTariffMutation>[0];

const handleDeleteTariff =
    (
        trigger: DeleteTariffMutationTrigger,
        dispatch: AppDispatch,
        businessId: number,
        tariffId: number,
        navigate: NavigateFunction
    ) =>
        async () => {
            try {
                dispatch(preloaderChangeStatus(true));
                await trigger({businessId, tariffId}).unwrap();
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
            } finally {
                navigate(AppRouteSpace([AppRouteEnum.app, AppRouteEnum.tariffs]));
                store.dispatch(preloaderChangeStatus(false));
            }
        };

export const DeleteTariff: FC<{ row: TariffDTO; toolbar?: boolean }> = ({row, toolbar}) => {
    const [trigger] = tariffsApi.useDeleteTariffMutation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const onDelete = handleDeleteTariff(trigger, dispatch, row.business.id, row.id, navigate);

    // Beside a page's other actions it is a labelled button, because "what does this icon do" is a
    // worse question to ask about the one irreversible action on the page. In a table row it stays
    // an icon: the row has a column for icons and no room for a word.
    if (toolbar) {
        return (
            <Button
                variant="contained"
                sx={actionButtonSx('32px', 'danger')}
                startIcon={<DeleteOutlineOutlined/>}
                onClick={onDelete}
            >
                <FormattedMessage id="delete"/>
            </Button>
        );
    }

    return (
        <IconButton onClick={onDelete}>
            <Delete/>
        </IconButton>
    );
};
